import styled from 'styled-components';

import { BUTTON_SIZES, Button, BUTTON_VARIANTS } from 'ui/button';
import { Modal, ModalHeader, ModalBody, MODAL_SIZES, ModalFooter, MODAL_VARIANTS } from 'ui/modal';
import { spacing } from 'ui/theme';

import { WorkflowIdData, WorkflowNameData } from '../../types';
import { TOGGLE_MODAL_DISPLAY_TEXT, TOGGLE_MODAL_TYPE } from '../../constants';

import { DisableWorkflowWarning } from './DisableWorkflowWarning';
import { EnableWorkflowWarning } from './EnableWorkflowWarning';
import { ToggleWorkflowPrompt } from './ToggleWorkflowPrompt';
import { BulkWorkflowList } from './BulkWorkflowList';

const ConfirmButton = styled(Button)`
  svg {
    margin-right: ${spacing.quarter};
  }
`;

type ToggleWorkflowDialogProps = {
  onClose: () => void;
  onConfirm: () => void;
  toggleType: TOGGLE_MODAL_TYPE;
  workflows: (WorkflowIdData & WorkflowNameData)[];
};

export const ToggleWorkflowDialog = ({
  onClose,
  onConfirm,
  toggleType,
  workflows,
}: ToggleWorkflowDialogProps) => {
  const isBulk = workflows.length > 1;
  const verb = TOGGLE_MODAL_DISPLAY_TEXT.verb[toggleType];
  const noun = isBulk
    ? TOGGLE_MODAL_DISPLAY_TEXT.noun.plural
    : TOGGLE_MODAL_DISPLAY_TEXT.noun.singular;
  const warning =
    toggleType === TOGGLE_MODAL_TYPE.ENABLE ? (
      <EnableWorkflowWarning isBulk={isBulk} />
    ) : (
      <DisableWorkflowWarning isBulk={isBulk} />
    );

  return (
    <Modal
      data-test="toggle-workflow-modal-wrapper"
      id="toggle-workflow-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader data-test="toggle-workflow-modal-header">
        {verb} {noun}?
      </ModalHeader>
      <ModalBody data-test="toggle-workflow-modal-body" id="toggle-workflow-modal-body">
        <ToggleWorkflowPrompt workflows={workflows} toggleType={toggleType} />
        {isBulk && <BulkWorkflowList workflows={workflows} />}
        {warning}
      </ModalBody>
      <ModalFooter
        cancelLabel={TOGGLE_MODAL_DISPLAY_TEXT.cancelButton}
        data-test="toggle-workflow-modal-actions"
        id="toggle-workflow-modal-actions"
      >
        <ConfirmButton
          onClick={onConfirm}
          size={BUTTON_SIZES.LARGE}
          variant={
            toggleType === TOGGLE_MODAL_TYPE.ENABLE
              ? BUTTON_VARIANTS.PRIMARY
              : BUTTON_VARIANTS.DESTRUCTIVE
          }
        >
          {isBulk ? `${verb} ${workflows.length} ${noun}` : `${verb} ${noun}`}
        </ConfirmButton>
      </ModalFooter>
    </Modal>
  );
};
